/**
 * [PM-SG 2026-08-11] PM Retention Grand Prix — the board.
 *
 * Five views over one payload, sized for a wall display and reached only by the shared
 * credentials account. It replaces a Google Apps Script web app; the visual language is
 * carried over deliberately, because that board was tuned in front of the actual television.
 *
 * FULL BLEED. Every other section renders inside the portal's rounded page card, on a gutter,
 * under a header. This one takes the viewport — a Formula 1 board framed by a white card with
 * a scrollbar would be a widget, not a board. It is a fixed overlay at z-index 60, which sits
 * under the floating profile circle (z-1200) so signing out is always reachable, and under the
 * nav dock (z-70) so the hairline stays lit.
 *
 * THE TELEVISION PROBLEM. The portal's UpdateBanner deliberately refuses to reload an
 * unattended tab: it shows a pill and waits for a human, because a reload that fails while
 * nobody is watching leaves a blank screen. A wall display is exactly that case and has no
 * human, so this board carves out its own path — it reloads itself, silently, when a deploy
 * lands. Two guards make that safe, and both are kept: the new build must actually SERVE
 * before we reload into it, and a build we have already reloaded towards is never chased
 * twice, so a half-served deploy cannot put the screen in a loop.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';
import RaceView from '../components/pmSingapore/RaceView';
import PitWallView from '../components/pmSingapore/PitWallView';
import TimingView from '../components/pmSingapore/TimingView';
import PodiumView from '../components/pmSingapore/PodiumView';
import PitDebriefView, { DEBRIEF_GROUP, DebriefVisit } from '../components/pmSingapore/PitDebriefView';
import NotifierView from '../components/pmSingapore/NotifierView';
import { warmNextGif } from '../components/pmSingapore/notifierGifs'; // [PM-SG GIF 2026-10-07]
import NavDock, { PageId, PAGES } from '../components/pmSingapore/NavDock';
import { INK, TEXT, RED, AMBER, FONT, EASE, prefersReducedMotion } from '../components/pmSingapore/chrome';
import {
  LeaderPayload, AnnouncementPayload, LeaderData, buildLeaderData, monthLabel,
} from '../components/pmSingapore/raceModel';

/* ─────────────────────────────────────────────────────────────── shell ── */

/**
 * The board owns the viewport.
 *
 * ⚠ `position: fixed` is NOT enough on its own here, and this cost a render to find. AppLayout
 * wraps every page in PageFade, whose mount animation runs with `fill-mode: both` — so the
 * element KEEPS `transform: translateY(0)` after it finishes. A transform of any value other
 * than `none` makes that element the containing block for fixed-position descendants, so
 * `inset: 0` resolved against the page card instead of the screen and the whole board was
 * squashed into the card's rounded box.
 *
 * Hence the portal to document.body below: it puts the board outside every ancestor that could
 * ever establish a containing block, and it does that without touching PageFade — whose
 * transform every other section in the portal relies on for its tab-switch animation.
 */
/**
 * [PM-SG CANVAS 2026-08-13] THE BOARD IS A FIXED CANVAS THAT GETS SCALED, NOT A FLUID LAYOUT.
 *
 * What was wrong: every size on this board was a `clamp(min, Nvw, max)` — 208 of them, each with
 * a px CEILING. Measured across the real resolutions, 17% of them hit that ceiling at 1080p and
 * 1440p, but **66% hit it at 4K**. So on a 4K panel two thirds of the board stopped growing while
 * the other third carried on: the composition didn't shrink evenly, it came apart. The worst was
 * `clamp(74px, 16vw, 120px)` on the race page — it wants 614px at 4K and was pinned to 120px,
 * five times too small.
 *
 * The fix is the one every broadcast graphic uses: compose once, on a fixed canvas, and scale the
 * whole thing to whatever screen it lands on. Every clamp and every vw/vh in the board was
 * converted to its value at exactly 1920x1080 — so this is a VISUAL NO-OP at 1080p (the size the
 * board was designed and signed off at) and every other resolution is now a clean uniform scale
 * of that. At 4K everything is exactly 2x: twice the size AND twice the detail.
 *
 * ⚠ CONSEQUENCE FOR ANY FUTURE EDIT: px in this board are CANVAS px, not screen px. 1920x1080 is
 * the coordinate space; do NOT reintroduce vw/vh/dvh anywhere inside the Stage — a viewport unit
 * resolves against the real screen, not the canvas, so it would grow independently of everything
 * around it and break the composition at exactly the resolutions this was built to fix. `em`, `%`
 * and `cq*` units are all fine: they're relative to something that is itself canvas-sized.
 *
 * ── [PM-SG FILL 2026-08-13] THE HEIGHT IS FIXED. THE WIDTH FOLLOWS THE SCREEN. ──
 *
 * A canvas locked to 16:9 letterboxes on anything that isn't 16:9 — and a browser window never is,
 * because the tab strip and address bar eat height. On a 1920x1080 laptop the usable viewport is
 * about 1920x900, which is 2.13:1, so the board sat inside two black bars roughly 160px wide and
 * read as though it wasn't using the screen.
 *
 * The height stays locked at CANVAS_H because that is what every type size and the whole vertical
 * rhythm were measured against — legibility must not drift with the window. The WIDTH is allowed
 * to follow the viewport's shape instead, and the layout absorbs it for free: every live page is
 * already fluid across its main axis (RACE's lane grid gives the track `1fr`, PIT WALL is
 * `repeat(6, 1fr)`, PARC FERME is `1fr 1.18fr 1fr`, TIMING is width:100%). So a wider screen
 * simply means a LONGER TRACK and more room for the labels riding beside the cars — no element
 * changes size, nothing is cropped, and the composition is untouched.
 *
 * On a true 16:9 screen the width works out to exactly 1920, so a television gets pixel-for-pixel
 * the composition that was designed and signed off. The clamp only bites on shapes far from 16:9
 * (a 4:3 monitor, a phone), where it stops the canvas becoming so narrow or so wide that the
 * fixed-width columns stop making sense — and there the board correctly letterboxes again.
 */
/** Which antialiasing the board renders text with — see the note on Board. */
type Smoothing = 'antialiased' | 'auto';
const SMOOTHING_KEY = 'pmsg-smoothing';

export const CANVAS_H = 1080;
/** What a 16:9 screen resolves to, and the width the board was composed at. */
export const CANVAS_DESIGN_W = 1920;
/** Roughly 1.48:1 to 2.41:1. Every screen in scope (16:10 through 21:9) lands inside this. */
const CANVAS_MIN_W = 1600;
const CANVAS_MAX_W = 2600;

/**
 * The letterbox. Fills the viewport, paints the bars, and centres the canvas inside itself.
 *
 * Bars are true black rather than INK so that on a non-16:9 screen the board reads as a screen
 * within a screen — the broadcast convention — instead of looking like the layout failed to
 * reach the edges.
 */
const Frame = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  background: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * The canvas itself: CANVAS_H tall always, as wide as the screen's shape asks for, scaled to fit.
 *
 * ⚠ WHY `zoom` AND NOT `transform: scale()`. Both scale the board correctly, but they differ in
 * the one thing this whole exercise is about — how text lands on a 4K panel. `transform` scales a
 * RASTER: the subtree is painted and then stretched, and any descendant carrying
 * `will-change: transform` (which the smoothness work adds to every animating element) gets
 * pinned to its own layer and can rasterise at the pre-scale resolution — soft text, on the exact
 * screen we're trying to sharpen. `zoom` scales the LAYOUT: a 16px label on a 4K screen is laid
 * out and rendered as a real 32px label, so it is genuinely twice the detail rather than twice the
 * size. The two goals would have fought each other; this is what keeps them aligned.
 *
 * `transform` is kept as the fallback for anything that can't do `zoom` (Firefox below 126) — it
 * scales identically, it just can't promise the same crispness.
 *
 * ⚠ MEASUREMENT TRAP, and it bit fitNames on the race page: under either mode `offsetWidth` /
 * `clientWidth` report CANVAS px (what layout code wants) while `getBoundingClientRect()` reports
 * SCREEN px. Mixing the two silently multiplies a measurement by the scale factor. Measure with
 * offset/client inside the Stage; reserve getBoundingClientRect for genuine screen coordinates.
 */
const Stage = styled.div<{ $scale: number; $zoom: boolean; $width: number }>`
  position: relative;
  width: ${(p) => p.$width}px;
  height: ${CANVAS_H}px;
  flex: 0 0 auto;
  ${(p) => (p.$zoom
    ? `zoom: ${p.$scale};`
    : `transform: scale(${p.$scale}); transform-origin: center center;`)}
`;

const Board = styled.div<{ $smoothing: Smoothing }>`
  position: absolute;
  inset: 0;
  z-index: 60;
  background: ${INK};
  color: ${TEXT};
  font-family: ${FONT};
  font-variant-numeric: tabular-nums;
  /**
   * [PM-SG TEXT 2026-08-13] SWITCHABLE, because which one is sharper depends on the screen and
   * cannot be decided from here.
   *
   * The antialiased setting turns OFF sub-pixel (RGB) antialiasing and renders glyphs in
   * greyscale. On a high-DPI panel that is the better look — thinner, no colour fringing, which is
   * why dark UIs reach for it. But on a DPR-1 display it throws away the horizontal resolution
   * sub-pixel rendering buys you, and small text goes soft. Both this laptop and a 4K television
   * driven by a browser normally report DPR 1, so the default here was quietly costing sharpness
   * on exactly the screens this board runs on.
   *
   * It is a genuine trade-off rather than a bug, so it is a toggle instead of a decision: press
   * the s key on the board to flip it, and the diagnostic overlay names the mode you are looking
   * at. The choice is remembered, so it survives the board's own deploy-reload.
   */
  -webkit-font-smoothing: ${(p) => p.$smoothing};
  -moz-osx-font-smoothing: ${(p) => (p.$smoothing === 'antialiased' ? 'grayscale' : 'auto')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* Bottom padding reserves the strip the nav line lives in, so its hover zone never overlaps
     the content above it. */
  /* Fluid rather than fixed: 20px of gutter either side is nothing on a 65" panel and a tenth of
     the usable width on a phone, where every pixel belongs to the figures. */
  padding: 16px 20px 36px;
  gap: 11.88px;

  /* Vignette. Above every board element but below the dock — it must not dim the glass pill. */
  &::after {
    content: '';
    position: absolute; inset: 0; pointer-events: none; z-index: 40;
    background: radial-gradient(ellipse at 50% 40%, transparent 55%, rgba(0, 0, 0, 0.45) 100%);
  }
`;

/** The board is a fixed overlay, so the page card behind it would still scroll and still show
 *  its own scrollbar. Locked while this section is mounted, and released on unmount. */
const LockScroll = createGlobalStyle`
  [data-page-scroll] { overflow: hidden !important; }
`;

const Banner = styled.div<{ $kind: 'warn' | 'err' }>`
  flex: 0 0 auto;
  padding: 11px 18px;
  border-radius: 3px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.09em;
  display: flex;
  align-items: center;
  gap: 14px;
  background: ${(p) => (p.$kind === 'warn' ? 'rgba(255,184,0,.13)' : 'rgba(225,6,0,.15)')};
  border-left: 5px solid ${(p) => (p.$kind === 'warn' ? AMBER : RED)};
  color: ${(p) => (p.$kind === 'warn' ? AMBER : '#FF6B5E')};

  .note { font-weight: 400; color: ${TEXT}; letter-spacing: 0.04em; }
`;

/**
 * [PM-SG 2026-08-12] THE PAGE CHANGE IS A KERB WIPE, not a cross-fade.
 *
 * The board changes page on its own now, and a dark cross-fade gave the room no way to tell a
 * scheduled change from the screen glitching. A red-and-white kerb band sweeps across, the swap
 * happens while it covers the screen, and it carries the name of the page arriving — so what a
 * passer-by sees is the board ANNOUNCING new data rather than quietly swapping it.
 *
 * Deliberately not the chequered flag: that one is spoken for. Chequered means a new BUILD is
 * being loaded, and two different events must never share a signal.
 */
/* [2026-09-18, user] 20% in, HOLD to 80%, out. At SWEEP_MS = 2000 that is 400ms to cover, 1.2s
   held on screen and 400ms to reveal - the transition is a beat the room can watch rather than a
   flicker, without becoming a wait on a rotation. Percentages, so changing SWEEP_MS rescales all
   of it; every variant shares these stops so SWEEP_COVER_MS is inside the hold whichever one is
   drawn. */
const kerbWipe = keyframes`
  0%   { transform: translateX(-128%) skewX(-14deg); }
  20%  { transform: translateX(0%) skewX(-14deg); }
  80%  { transform: translateX(0%) skewX(-14deg); }
  100% { transform: translateX(128%) skewX(-14deg); }
`;
/**
 * ⚠ [PM-SG SMOOTH 2026-08-13] WAS A clip-path ANIMATION. It is now a transform, and the reason is
 * the whole point of the smoothness work: `clip-path` is not GPU-composited in Chrome, so every
 * frame of this repainted the FULL screen on the main thread — 8.3 million pixels a frame at 4K,
 * during the two seconds the room is most likely to be looking at the board. `transform` is
 * handed to the compositor and costs the main thread nothing.
 *
 * ONE DELIBERATE VISUAL CHANGE: the clip version revealed the page name in place as the door
 * passed over it; the door now carries its name down with it and lifts it away again. That is
 * what a real pit shutter does, and it is the version that costs nothing to draw. Everything else
 * — the drop, the hold, the lift, the two-second total — is unchanged.
 */
const shutter = keyframes`
  0%   { transform: translateY(-100%); }
  20%  { transform: translateY(0); }
  80%  { transform: translateY(0); }
  100% { transform: translateY(-100%); }
`;
const lightsOut = keyframes`
  0%   { opacity: 0; }
  14%  { opacity: 1; }
  86%  { opacity: 1; }
  100% { opacity: 0; }
`;
const lampOn = keyframes`
  from { background: #2A1114; box-shadow: inset 0 -3px 7px rgba(0,0,0,.55); }
  to   { background: ${RED}; box-shadow: 0 0 26px rgba(225,6,0,.95), inset 0 -3px 7px rgba(0,0,0,.45); }
`;
const titleIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(.94); filter: blur(8px); }
`;

/* ─────────────────────────────────────────────────── the hold, kept alive ── */
/*
 * [2026-09-18, user] EVERYTHING BELOW EXISTS BECAUSE A HELD FRAME READS AS A CRASH.
 *
 * At three seconds the transition covers by 600ms and starts revealing at 2400ms, and until now
 * NOTHING moved in between except a 420ms title entrance - so the board sat on a frozen image for
 * about 1.4 seconds. The user's report was exactly right: *"feels static or broken"*. A viewer
 * cannot tell a deliberate pause from a hung screen, and on a wall display nobody is there to
 * click anything to find out.
 *
 * So every variant now carries motion for the WHOLE covered window, and the timing bar below is
 * shared by all five - it is the one element that guarantees the screen is visibly counting down
 * rather than stuck, whichever transition was drawn.
 */

/** The shared timing bar: fills left to right across the covered window, then clears. */
const sweepBar = keyframes`
  0%   { transform: scaleX(0); opacity: 0; }
  18%  { opacity: 1; }
  78%  { transform: scaleX(1); opacity: 1; }
  100% { transform: scaleX(1); opacity: 0; }
`;

/** Kerb stripes running, so the band reads as track furniture rather than a painted rectangle. */
const kerbRun2 = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 168px 0; }
`;

/** The shutter's scanlines drifting down, the way a real roller door shows its slats moving. */
const slatDrift = keyframes`
  from { background-position: 0 0, 0 0; }
  to   { background-position: 0 44px, 0 0; }
`;

/** A light bar travelling across the closed shutter. */
const shutterGleam = keyframes`
  0%, 12%   { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
  26%       { opacity: .5; }
  74%       { transform: translateX(130%) skewX(-18deg); opacity: .5; }
  100%      { opacity: 0; }
`;

/**
 * THE START SEQUENCE, PROPERLY. Five lamps light one at a time, hold, and then GO OUT together
 * just before the board opens - which is the actual thing an F1 start is. It was the most static
 * variant of the five and is now the one with the most meaning: the lights going out IS the
 * page arriving.
 */
const lampsOut = keyframes`
  from { background: ${RED}; box-shadow: 0 0 26px rgba(225,6,0,.95), inset 0 -3px 7px rgba(0,0,0,.45); }
  to   { background: #2A1114; box-shadow: inset 0 -3px 7px rgba(0,0,0,.55); }
`;

/** The chequer scrolling under the split halves. */
const chequerRun = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 172.8px 0; }
`;

/** The title breathing, so even the text is never a still object. */
/* The transition title breathes on OPACITY now, not on a glow: the page name is text, and text
   on this board does not carry a shadow. */
const titleHold = keyframes`
  0%, 100% { opacity: 0.88; }
  50%      { opacity: 1; }
`;
/** Two halves of a chequered board closing on the middle and parting again. */
/* ⚠ 20/80 like every other variant. These two were left at 34/66 when the sweep went to three
   seconds, which put full cover at 1020ms - AFTER the 900ms page swap. The board flipped pages
   in front of the viewer on this variant and only this one. Any new variant must cover by 20%. */
const splitTop = keyframes`
  0%   { transform: translateY(-100%); }
  20%  { transform: translateY(0); }
  80%  { transform: translateY(0); }
  100% { transform: translateY(-100%); }
`;
const splitBottom = keyframes`
  0%   { transform: translateY(100%); }
  20%  { transform: translateY(0); }
  80%  { transform: translateY(0); }
  100% { transform: translateY(100%); }
`;
/** Tyre smoke: a wall of it rolls over the screen and clears. */
const smokeRoll = keyframes`
  0%   { opacity: 0; transform: translateX(-38%) scale(1.5); }
  26%  { opacity: 1; }
  70%  { opacity: 1; }
  100% { opacity: 0; transform: translateX(38%) scale(1.5); }
`;
const smokeBed = keyframes`
  0%   { opacity: 0; }
  22%  { opacity: 1; }
  74%  { opacity: 1; }
  100% { opacity: 0; }
`;

/**
 * FIVE OF THEM, PICKED AT RANDOM — a transition seen twenty times an hour stops being an event
 * if it never varies. Each is a different piece of the sport's own language:
 *
 *   0 KERB WIPE    a red-and-white kerb band sweeps across and holds
 *   1 PIT SHUTTER  the garage door drops, holds, and lifts away
 *   2 LIGHTS OUT   the gantry lights five lamps, then everything goes dark and releases
 *   3 CHEQUER SPLIT two chequered halves close on the middle and part again
 *   4 TYRE SMOKE   a wall of smoke rolls over the screen and clears
 *
 * All five carry the name of the page arriving, all five are opaque while the swap happens
 * underneath, and all five run to the same two seconds — so the rotation's timing never depends
 * on which one comes up.
 *
 * ⚠ Random, but never the SAME one twice running: a repeat reads as the board stuttering rather
 * than as variety. The draw is re-rolled against the previous pick in `go`.
 *
 * ⚠ Variant 3 uses a chequered pattern but is NOT the full-screen chequered flag, which is
 * reserved: that one means a new BUILD is loading. This one is split in half, moves vertically
 * and carries a page name, so the two can never be confused.
 */
const Sweep = styled.div<{ $kind: number }>`
  /* [PM-SG CANVAS] Absolute, not fixed: it lives INSIDE the Stage so its band and its page name
     scale with the canvas. inset:0 covers the whole canvas, which is the whole visible board —
     the letterbox bars beyond it are black anyway. */
  position: absolute;
  inset: 0;
  z-index: 68;
  overflow: hidden;
  pointer-events: none;

  .band {
    position: absolute;
    inset: -14% -45%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 15.12px;
  }

  ${(p) => p.$kind === 0 && css`
    .band {
      background: linear-gradient(90deg,
        transparent 0%, rgba(225, 6, 0, 0.85) 2.5%, #14141C 8%,
        #14141C 92%, rgba(225, 6, 0, 0.85) 97.5%, transparent 100%);
      animation: ${kerbWipe} ${SWEEP_MS}ms cubic-bezier(.5, 0, .35, 1) both;
    }
    .band::before {
      content: '';
      position: absolute; left: 3.4%; top: 0; bottom: 0; width: 1.1%;
      background: repeating-linear-gradient(115deg, ${RED} 0 14px, #F2F2F2 14px 28px);
      background-size: 168px 168px;
      opacity: 0.9;
      animation: ${kerbRun2} 900ms linear infinite;
    }
    /* A second kerb on the trailing edge, running the other way — the band now has two moving
       rails instead of one painted one. */
    .band::after {
      content: '';
      position: absolute; right: 3.4%; top: 0; bottom: 0; width: 1.1%;
      background: repeating-linear-gradient(115deg, ${RED} 0 14px, #F2F2F2 14px 28px);
      background-size: 168px 168px;
      opacity: 0.9;
      animation: ${kerbRun2} 900ms linear infinite reverse;
    }
    .t { transform: skewX(14deg); }
  `}

  ${(p) => p.$kind === 1 && css`
    .band {
      inset: 0;
      background:
        repeating-linear-gradient(180deg, rgba(255,255,255,.045) 0 2px, transparent 2px 22px),
        linear-gradient(180deg, #1B1B24, #101017);
      background-size: 100% 44px, 100% 100%;
      border-bottom: 5.4px solid ${RED};
      animation:
        ${shutter} ${SWEEP_MS}ms cubic-bezier(.6, 0, .3, 1) both,
        ${slatDrift} 1100ms linear infinite;
    }
    .band::after {
      content: '';
      position: absolute; top: 0; bottom: 0; left: 0; width: 26%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,.16), transparent);
      animation: ${shutterGleam} ${SWEEP_MS}ms ease-in-out both;
    }
  `}

  ${(p) => p.$kind === 2 && css`
    .band {
      inset: 0;
      background: #0B0B10;
      animation: ${lightsOut} ${SWEEP_MS}ms ease both;
    }
    .lamps { display: flex; gap: 19.2px; }
    .lamps i {
      display: block;
      width: 34.56px; height: 34.56px;
      border-radius: 50%;
      background: #2A1114;
      box-shadow: inset 0 -3px 7px rgba(0, 0, 0, 0.55);
      animation: ${lampOn} 120ms ease forwards;
    }
    .lamps i:nth-child(1) { animation-delay: 260ms; }
    .lamps i:nth-child(2) { animation-delay: 420ms; }
    .lamps i:nth-child(3) { animation-delay: 580ms; }
    .lamps i:nth-child(4) { animation-delay: 740ms; }
    .lamps i:nth-child(5) { animation-delay: 900ms; }
    /* ...and then they go out, together, right before the board opens. Lights out and away we go.
       160ms is a real start-light extinguish, not a fade. */
    .lamps i {
      /* ⚠ DERIVED, not a literal. This was 2180ms when the sweep was 3000ms; left alone at
         SWEEP_MS = 2000 the lights would have gone out 580ms AFTER the board had already
         opened — the one beat on this variant, played to nobody. 0.69 puts the extinguish at
         1380ms, finishing 60ms before the reveal at 1600ms. */
      animation:
        ${lampOn} 120ms ease forwards,
        ${lampsOut} 160ms ease ${SWEEP_MS * 0.69}ms forwards;
    }
  `}

  ${(p) => p.$kind === 3 && css`
    .band { inset: 0; background: none; }
    .half {
      position: absolute; left: 0; right: 0; height: 50%;
      background: repeating-conic-gradient(#E9E9F0 0 25%, #14141C 0 50%) 0 0 / 86.4px 86.4px;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
    }
    /* The chequer runs while the halves are closed, in opposite directions — the flag is waving,
       not printed. */
    /* ⚠ LONGHANDS ONLY, and no shorthand after them. An animation shorthand resets every
       longhand it does not name, so one written below these lines silently drops the second
       (chequer) animation and the flag goes back to being printed. (No backticks in here: this
       comment lives inside a template literal and a backtick ends it.) */
    .half.t { top: 0;    animation-name: ${splitTop}, ${chequerRun}; }
    .half.b { bottom: 0; animation-name: ${splitBottom}, ${chequerRun}; animation-direction: normal, reverse; }
    .half.t, .half.b {
      animation-duration: ${SWEEP_MS}ms, 1400ms;
      animation-timing-function: cubic-bezier(.65, 0, .3, 1), linear;
      animation-iteration-count: 1, infinite;
      animation-fill-mode: both, none;
    }
    /* The name rides ABOVE both halves, on its own plate, so it stays legible over the pattern. */
    .t {
      z-index: 2;
      padding: 0.4em 1em;
      background: rgba(10, 10, 14, 0.92);
    }
  `}

  ${(p) => p.$kind === 4 && css`
    .band {
      inset: 0;
      background: linear-gradient(180deg, rgba(18,18,26,.92), rgba(10,10,14,.97));
      animation: ${smokeBed} ${SWEEP_MS}ms ease both;
    }
    .puff {
      position: absolute; inset: -20% -30%;
      background:
        radial-gradient(circle at 22% 62%, rgba(226, 230, 240, 0.5), transparent 46%),
        radial-gradient(circle at 54% 38%, rgba(214, 220, 236, 0.42), transparent 44%),
        radial-gradient(circle at 78% 66%, rgba(236, 240, 250, 0.38), transparent 46%),
        radial-gradient(circle at 38% 22%, rgba(200, 206, 222, 0.3), transparent 40%);
      filter: blur(26px);
      animation: ${smokeRoll} ${SWEEP_MS}ms ease-in-out both;
    }
  `}

  .t {
    position: relative;
    z-index: 3;
    font-family: ${FONT};
    font-size: 46px;
    font-weight: 900;
    letter-spacing: 0.3em;
    color: ${TEXT};
    text-shadow: none;
    white-space: nowrap;
    animation:
      ${titleIn} 420ms ${EASE} 260ms both,
      ${titleHold} 1600ms ease-in-out 700ms infinite;
  }

  /* THE TIMING BAR. Shared by all five variants and the reason none of them can read as frozen:
     whatever else is on screen, this is visibly counting the transition down. Sized in canvas px
     and sitting under the page name. */
  .prog {
    position: relative;
    z-index: 3;
    width: 340px;
    height: 5px;
    border-radius: 3px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.12);
  }
  .prog i {
    display: block;
    width: 100%;
    height: 100%;
    transform-origin: left center;
    background: linear-gradient(90deg, ${RED}, #FF6B5E);
    box-shadow: 0 0 14px rgba(225, 6, 0, 0.8);
    /* A function interpolation, like the per-variant blocks above: SWEEP_MS is declared further
       down the file, and a top-level template reads it at module load, before it exists. */
    ${() => css`animation: ${sweepBar} ${SWEEP_MS}ms cubic-bezier(.3, .1, .3, 1) both;`}
  }
`;

/* The plain dark cross-fade that used to sit here is gone — the kerb wipe above replaced it. */

const Boot = styled.div`
  /* [PM-SG CANVAS] Absolute — it is board content and scales with the canvas. */
  position: absolute;
  inset: 0;
  z-index: 66;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  background: ${INK};
  color: ${TEXT};
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.34em;

  /* The start-lights gantry lives INSIDE the boot screen. On the board itself it would be
     covered by this overlay, which is what made the sequence invisible the first time. */
  .lights {
    display: flex; gap: 15px;
  }
  .lights i {
    width: 28px; height: 28px; border-radius: 50%; display: block; background: #2A1114;
    box-shadow: inset 0 -3px 7px rgba(0, 0, 0, 0.55);
    animation: lightOn 0.16s ease forwards;
  }
  .lights i:nth-child(1) { animation-delay: .22s; }
  .lights i:nth-child(2) { animation-delay: .46s; }
  .lights i:nth-child(3) { animation-delay: .70s; }
  .lights i:nth-child(4) { animation-delay: .94s; }
  .lights i:nth-child(5) { animation-delay: 1.18s; }
  @keyframes lightOn {
    to { background: ${RED}; box-shadow: 0 0 20px rgba(225,6,0,.9), inset 0 -3px 7px rgba(0,0,0,.45); }
  }
`;

/**
 * [PM-SG 2026-08-12] THE CHEQUERED FLAG OVER A RELOAD.
 *
 * The board already updates itself when a new build lands — it polls version.json and reloads
 * without anybody touching it. What it did NOT do was cover the reload: the page simply went
 * white for as long as the browser took to fetch and paint the new bundle, which on a wall in
 * front of the whole floor reads as the board crashing.
 *
 * A chequered flag wipes across, the reload happens underneath it, and the flag is still up when
 * the new document paints — so what the room sees is a flag, and then a board carrying the new
 * figures. It is also the honest F1 signal for it: chequered means THIS session is over, and the
 * boot screen's start lights on the other side mean the next one is beginning.
 */
/**
 * ⚠ [PM-SG SMOOTH 2026-08-13] WAS a clip-path reveal, for exactly the reason given on `shutter`:
 * clip-path repaints the whole screen on the main thread every frame. The flag now sweeps in from
 * the left as a transform instead — which is also closer to what a flag actually does — and the
 * pattern travels with it rather than appearing in place. Same 420ms, same coverage.
 */
const flagWipe = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

/**
 * [PM-SG 2026-08-13] THE FLAG IS WAVED, not just held up.
 *
 * ⚠ HOW, and why it is built out of strips rather than done the obvious way. The obvious way is an
 * SVG turbulence + displacement filter, which gives real cloth — and which would repaint the FULL
 * SCREEN on the main thread every frame for five seconds. At 4K that is 8.3 million pixels a frame,
 * on the one element that covers everything, at the exact moment the board is also loading a new
 * bundle underneath itself. It would undo the whole smoothness effort in the place it matters most.
 *
 * So the cloth is FLAG_STRIPS vertical panels, each of which only ever TRANSLATES. Give each one
 * the same oscillation with a slightly later start and the crests walk across the screen: a
 * travelling wave, entirely on the compositor, at a cost of a dozen-odd tiny layers.
 *
 * ⚠ The seam trick: every strip carries the same chequer, but its background is pushed LEFT by its
 * own distance from the edge, so the pattern reads as one continuous flag rather than N tiles that
 * happen to sit next to each other. The offset is per-strip inline style, because it depends on the
 * real viewport width and CSS percentages in background-position resolve against the wrong box.
 *
 * Strips are over-tall and over-wide on purpose — 104% high, +1px each — so a crest can never
 * expose the edge of the screen and adjacent panels can never show a hairline between them.
 */
const flagWave = keyframes`
  0%, 100% { transform: translateY(-1.15%) scaleY(0.993); }
  50%      { transform: translateY(1.15%) scaleY(1.007); }
`;

/** Enough panels for the crests to read as cloth rather than as bars; cheap at this size. */
const FLAG_STRIPS = 18;
/** One full ripple, and the per-strip stagger that turns it into a travelling one. */
const FLAG_WAVE_MS = 1500;
const FLAG_WAVE_STEP_MS = 78;

const Chequer = styled.div<{ $scale: number }>`
  position: fixed;
  inset: 0;
  /* Above everything, including the floating profile at 1200 — nothing may show through a flag. */
  z-index: 1400;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => 22 * p.$scale}px;
  overflow: hidden;
  /* The dark of the chequer, so that even mid-crest there is never a gap to see through. */
  background: #14141C;
  animation: ${flagWipe} 420ms cubic-bezier(.4,.02,.2,1) both;

  .cloth {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }
  /* Squares scaled to the panel rather than fixed at 128px — on a 4K television a 128px chequer
     is confetti, where this stays a flag at any size. Sitting outside the Stage, this one does not
     get the canvas scale for free, so it is applied by hand — otherwise the flag would be the one
     element on the board that shrank as the screen grew. */
  .cloth i {
    position: absolute;
    top: -2%;
    height: 104%;
    width: calc(100% / ${FLAG_STRIPS} + 1px);
    background: repeating-conic-gradient(#E9E9F0 0 25%, #14141C 0 50%) 0 0
      / ${(p) => 172.8 * p.$scale}px ${(p) => 172.8 * p.$scale}px;
    animation: ${flagWave} ${FLAG_WAVE_MS}ms ease-in-out infinite;
    will-change: transform;
  }

  @media (prefers-reduced-motion: reduce) {
    .cloth i { animation: none; }
  }

  span {
    /* Above the cloth, and deliberately NOT waved: the flag moves, the words hold still so they
       stay readable from across the room. */
    position: relative;
    z-index: 1;
    padding: 0.7em 1.4em;
    background: rgba(10, 10, 14, 0.92);
    color: ${TEXT};
    font-family: ${FONT};
    /* Read from across the room, like everything else on this board. */
    font-size: ${(p) => 32.64 * p.$scale}px;
    font-weight: 900;
    letter-spacing: 0.34em;
    text-align: center;
  }
`;

/* ─────────────────────────────────────────────────────────── polling ── */

const POLL_MS = 30000;
/** How long the flag is held before the reload fires, so it is seen rather than glimpsed. */
/**
 * ⚠ [2026-08-12] 2.5s, up from 0.9s. The flag was technically working and effectively invisible:
 * nine hundred milliseconds plus however long the reload takes is a blink, and on a wall nobody
 * is looking at the exact instant it happens. It is the only signal the room gets that the board
 * has just taken a new build, so it has to last long enough to be SEEN and read, not merely to
 * have occurred. The reload runs underneath it either way, so the wait costs nothing.
 *
 * ⚠ [2026-08-13] 5s, on request, and this is the number to change if it is ever revisited.
 *
 * What is actually on screen is LONGER than this figure: the flag is deliberately never torn down
 * before the reload, so after these 5 seconds it stays painted for however long the new document
 * takes to load and paint underneath it — call it another second. So 5s here reads as about six
 * seconds of chequered flag in the room. The 420ms wipe-in at the top of the Chequer rule is part
 * of this window, not added to it.
 *
 * It costs nothing but the delay before the board picks up the new build, which is already up to
 * VERSION_POLL_MS behind anyway.
 */
const FLAG_MS = 5000;
/**
 * The transition's full run.
 *
 * [2026-09-18, user] 2000 -> 3000 -> 2000. Three seconds proved too long to sit through on a
 * rotation; two keeps the page change an event without becoming a wait. The window still holds
 * long enough for the motion below to read, because every variant is percentage-based.
 *
 * ⚠ THE SWEEP CSS READS THIS CONSTANT DIRECTLY NOW. Every variant's duration used to be a
 * hardcoded 2000ms sitting a few hundred lines away from this line, with only a comment asking
 * the next person to keep them in step. Changing one and not the other reveals the swap.
 */
const SWEEP_MS = 2000;
/**
 * Where in that run the screen is fully covered, and therefore where the page is swapped.
 *
 * ⚠ MUST FALL INSIDE THE HOLD. The keyframes cover at 20% and start revealing at 80%, so at
 * SWEEP_MS = 2000 the safe window is 400ms..1600ms. At 900ms the swap happens 500ms after the
 * screen is solid and 700ms before it opens again — nobody can see the page change underneath.
 * ⚠ Shortening SWEEP_MS shrinks that window at BOTH ends. Below ~1200ms this constant has to
 * come down with it or the swap lands outside the cover.
 */
const SWEEP_COVER_MS = 900;
/** How many transition variants exist. ⚠ Must match the $kind branches on Sweep. */
const SWEEP_KINDS = 5;
/**
 * [PM-SG 2026-08-12] HOW LONG A BLIP IS ALLOWED TO LAST BEFORE THE BOARD SAYS ANYTHING.
 *
 * The page used to raise CONNECTION LOST on a SINGLE failed request. On a wall display that is
 * a false alarm nearly every time it fires: an API restart, a deploy, one dropped packet — all
 * of them are over within seconds, and the figures already on the screen are still perfectly
 * good. What actually deserves the banner is STALENESS: the board has been unable to refresh
 * for long enough that what is on the wall might now be wrong.
 *
 * So the rule is time-based, not attempt-based. Forty-five seconds is one and a half poll
 * cycles — long enough that a restart never reaches the screen, short enough that a real
 * outage is announced before anybody has acted on an old number.
 */
const STALE_MS = 45000;
/** A failed poll retries in four seconds instead of waiting the full cycle, so the usual cause
 *  (the API coming back up) heals long before the staleness window is anywhere near expiring. */
const RETRY_MS = 4000;
/**
 * [PM-SG 2026-08-12] Halved to 30s. This is DETECTION granularity, not the time to the screen —
 * see SETTLE_MS below, which is what actually sets when the board takes a new build. Keeping the
 * poll short means the settle starts promptly and lands predictably; it is one conditional GET of
 * a tiny static file, so the cost is nothing.
 */
const VERSION_POLL_MS = 30000;
/**
 * [PM-SG SETTLE 2026-08-13] HOW LONG A NEW BUILD MUST HAVE BEEN VISIBLE BEFORE THE BOARD TAKES IT.
 *
 * Asked for by the user on 2026-08-13: a deploy should reach the wall after about three minutes.
 * The board's earlier target was two, met by reloading within ~35 seconds of the new version.json
 * appearing — effectively no wait at all.
 *
 * It buys real safety on top of the number. `npm run build` rewrites build/ IN PLACE, so a fresh
 * version.json can advertise a build whose chunks are still being written. The board only had the
 * index.html probe below guarding that, and a probe can pass in the same window a lazily-loaded
 * chunk is still half-written. This is the same three-minute hold the portal has run since
 * 2026-07-30 (UpdateBanner SETTLE_MS) after a build that reloaded the instant version.json changed
 * landed mid-deploy and served nginx 500s. The board never had it. Now both agree.
 *
 * ⚠ The wall clock from deploy to new build on screen is a little OVER three minutes, not exactly
 * three: up to VERSION_POLL_MS to notice it at all, then this hold, then FLAG_MS of chequered flag.
 * Call it 3:05 to 3:35. The portal's comment records the same property for the same reason. The
 * board keeps showing live figures throughout — nothing is frozen while it waits.
 */
const SETTLE_MS = 3 * 60_000;
/** How long a failed reload blacklists a build id before the board is allowed to try again. */
const RELOAD_RETRY_MS = 300000;

/**
 * The circuit the board drives on its own, with a dwell PER STOP rather than one figure for all
 * of them — PIT WALL is five figures and is read in a glance, where RACE and TIMING carry ten
 * rows each and are not.
 *
 * RACE is the hub, not a stop in a ring: every other page is visited FROM it and returns TO it.
 * PODIUM and NOTIFIER are deliberately not in here — those are shown on purpose, not on a timer.
 */
/**
 * THE CIRCUIT.
 *
 * RACE is the hub and every other page is a visit from it: RACE → a guest → RACE → a different
 * guest → and so on, for ever. The guest is drawn at RANDOM from the three rather than taken in
 * order, so nobody watching can predict what is coming next — which is the difference between a
 * board people glance at and one they stop looking at because they know what it says.
 *
 * Never the same guest twice running: a repeat looks like the rotation is stuck.
 */
/* [PM-SG HALL 2026-09-01, user] ROLL OF HONOUR joins as a fourth guest, shown the same way
   TIMING and PARC FERMÉ are. ⚠ Each existing guest's share drops from a third to a quarter;
   that is the intended trade — the record is worth as much wall time as the other three. */
const GUESTS: PageId[] = ['pitwall', 'timing', 'podium', 'debrief'];

/** How long each page holds the screen. PIT WALL is five figures read in a glance; the others
 *  are ten rows or three cards and need longer. */
const DWELL: Record<PageId, number> = {
  /* [2026-09-18, user] EVERY STOP IS 90 SECONDS. RACE for a minute and a half, then one guest
     drawn at random for a minute and a half, then back to RACE. PIT WALL used to get 60s on the
     grounds that five figures are read in a glance - but an uneven circuit makes the board feel
     like it is skipping a page, and the room now reads the benchmark and the pip count there
     too. One number for every stop, which is also one number to change. */
  race: 90000,
  pitwall: 90000,
  timing: 90000,
  podium: 90000,
  debrief: 90000,
  notifier: 90000,
};

/**
 * A new announcement takes the screen for fifteen seconds, then the board goes back to exactly the
 * page it was interrupted on — the circuit resumes where it left off rather than restarting.
 *
 * Deliberately short: the card carries one name and one figure, and holding a minute on a page
 * that is read in three seconds is how an interruption turns into an annoyance. Twenty originally;
 * the user cut it to ten on 2026-08-13 after watching it on the television.
 *
 * ⚠ [2026-09-18, user] THIRTY. The card carries a name, a figure and the money that moved, and
 * fifteen seconds was not long enough to read it and look back up. The interrupt still resumes on
 * exactly the page it took the screen from, so the circuit is not restarted.
 *
 * ⚠ IT IS COUPLED TO THE SOUND. The cue runs 7.55s (sound.ts: 0.95s alert, five seconds of bed at
 * level, a 1.6s staged fade). Audio still playing after the card has gone is heard as the board
 * talking over itself, so this must stay comfortably ABOVE PLAY_S — which it now is, by 22s.
 * PLAY_S carries the same warning pointing back here.
 */
const ANNOUNCE_MS = 30000;

/** A human who touches the dock owns the board until they have been idle this long. */
const MANUAL_IDLE_MS = 60000;
const RELOAD_KEY = 'pmsg-reload-to';

/** Read straight through fetch rather than the shared axios client: that client keeps a
 *  five-minute stale-while-revalidate cache for instant tab switching, which is exactly wrong
 *  for a board that polls itself and must never show a figure older than its own cycle. */
async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include', headers: { 'Cache-Control': 'no-store' } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

/**
 * [PM-SG CANVAS 2026-08-13] `zoom` where we can have it, `transform` where we can't.
 *
 * Resolved ONCE at module load rather than per render: it cannot change during a session, and the
 * board must never swap scaling mode mid-flight — that would re-layout every element on screen.
 * Chrome, Edge and Safari all report true here; Firefox does from 126.
 */
const CAN_ZOOM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '2');

interface CanvasFit {
  /** Screen px per canvas px. */
  scale: number;
  /** Canvas width in canvas px — CANVAS_DESIGN_W on any 16:9 screen. */
  width: number;
}

/**
 * [PM-SG FILL 2026-08-13] Work out the canvas that exactly fills this screen.
 *
 * The height is the fixed side, so the scale wants to be `vh / CANVAS_H` and the width that
 * exactly fills at that scale is `vw / scale` — which is just the viewport's aspect ratio times
 * CANVAS_H. Clamping that width is what keeps a freak shape (a phone held upright, a 4:3 monitor)
 * from producing a canvas too narrow for the fixed-width columns or absurdly wide.
 *
 * The `min` on the last line is what makes the clamp behave: while the ideal width is inside the
 * clamp, `vw / width` and `vh / CANVAS_H` are the SAME number and the canvas fills edge to edge
 * with no bars. Once the clamp bites, the two diverge and `min` falls back to fitting the canvas
 * inside the screen — letterboxed, proportions intact. One expression covers both cases.
 */
function fitCanvas(): CanvasFit {
  if (typeof window === 'undefined') return { scale: 1, width: CANVAS_DESIGN_W };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (vw <= 0 || vh <= 0) return { scale: 1, width: CANVAS_DESIGN_W };
  const ideal = (vw * CANVAS_H) / vh;
  const width = Math.round(Math.min(CANVAS_MAX_W, Math.max(CANVAS_MIN_W, ideal)));
  return { scale: Math.min(vw / width, vh / CANVAS_H), width };
}

function useCanvasFit(): CanvasFit {
  const [fit, setFit] = useState(fitCanvas);
  useEffect(() => {
    const apply = () => setFit((prev) => {
      const next = fitCanvas();
      // Same numbers → same object, so a resize event that changes nothing (a Chrome scrollbar
      // settling, a TV reporting twice) cannot re-render the whole board for no reason.
      return prev.scale === next.scale && prev.width === next.width ? prev : next;
    });
    apply(); // the first paint may predate the real viewport (TV browsers report late)
    window.addEventListener('resize', apply);
    // A television can change resolution under a running page (HDMI renegotiation, a source
    // switch); orientationchange is the phone/tablet equivalent. Both must re-fit.
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);
  return fit;
}

/**
 * [PM-SG DIAG 2026-08-13] What the board is ACTUALLY getting, read off the screen it is on.
 *
 * Toggled with `d`, hidden by default, and it exists because the two things that decide whether
 * this board can be smooth and sharp — the panel's real refresh rate and the real viewport it is
 * handed — cannot be known from here. A cast path silently re-encodes at ~30fps whatever we
 * build; a direct HDMI path does not. This tells you which one you are on, in one keypress.
 *
 * FPS is measured over a rolling second of animation frames, so it reports frames the board
 * actually SHIPPED, not frames it hoped for. `Hz` is the highest FPS seen while idle, which is
 * the closest a page can get to asking the panel its refresh rate.
 */
const Diag = styled.div`
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 2000;
  padding: 10px 14px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre;
  pointer-events: none;
`;

const Diagnostics: React.FC<{ fit: CanvasFit; smoothing: Smoothing }> = ({ fit, smoothing }) => {
  const { scale, width } = fit;
  const [fps, setFps] = useState(0);
  const peak = useRef(0);

  useEffect(() => {
    let raf = 0;
    let frames = 0;
    let mark = performance.now();
    const tick = (t: number) => {
      frames += 1;
      if (t - mark >= 1000) {
        const v = Math.round((frames * 1000) / (t - mark));
        peak.current = Math.max(peak.current, v);
        setFps(v);
        frames = 0;
        mark = t;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  return (
    <Diag>
      {[
        `fps        ${fps}   (peak ${peak.current})`,
        `viewport   ${vw} x ${vh}   dpr ${dpr}`,
        `device px  ${Math.round(vw * dpr)} x ${Math.round(vh * dpr)}`,
        `canvas     ${width} x ${CANVAS_H}  @ ${scale.toFixed(3)}x  (${CAN_ZOOM ? 'zoom' : 'transform'})`,
        `fills      ${Math.round(width * scale)} x ${Math.round(CANVAS_H * scale)}   bars ${Math.max(0, Math.round((vw - width * scale) / 2))}px each side`,
        `text AA    ${smoothing}${smoothing === 'antialiased' ? ' (greyscale)' : ' (sub-pixel)'}   — press s to compare`,
      ].join('\n')}
    </Diag>
  );
};

const PMSingaporePage: React.FC = () => {
  const [race, setRace] = useState<LeaderData | null>(null);
  const [feed, setFeed] = useState<AnnouncementPayload | null>(null);
  /* [PM-SG DEBRIEF 2026-09-18] Which group of three the debrief shows next.
     ⚠ A REF, not state inside the view. Every view here is conditionally rendered, so
     PitDebriefView genuinely unmounts when the circuit moves on — a useState in there would
     reset to the first three for ever and the wall would only ever show ranks 4-6. */
  const debriefPage = useRef(0);
  /* [PM-SG DEBRIEF ONE-CARD 2026-09-21] The visit on screen (its three ranks fixed at arrival), the
     card of it that is up — written by the view — and whether the next arrival is the board
     coming BACK from an announcement, which reopens the same visit on the same card. */
  const [debrief, setDebrief] = useState<DebriefVisit | null>(null);
  const debriefVisit = useRef<DebriefVisit | null>(null);
  const debriefCard = useRef(0);
  /** When that card came up, so a re-armed circuit counts only what is LEFT of its share. */
  const debriefCardAt = useRef(0);
  const debriefResume = useRef(false);
  const onDebriefCard = useCallback((i: number) => {
    debriefCard.current = i;
    debriefCardAt.current = Date.now();
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<PageId>('race');
  /** The page the transition is announcing and which of the three is playing, or null when idle. */
  const [sweep, setSweep] = useState<{ page: PageId; kind: number } | null>(null);
  const sweepKind = useRef(-1);
  const [entryToken, setEntryToken] = useState(0);
  const [booting, setBooting] = useState(true);
  /** A new build has been confirmed to serve: the flag is out and the reload is coming. */
  const [flagging, setFlagging] = useState(false);
  /** When the board last held a fresh payload, and how many polls have missed since. */
  const lastOk = useRef(0);
  const misses = useRef(0);
  const retry = useRef(0);
  /** Latches the one-time hand-off from the boot screen to the board. */
  const booted = useRef(false);
  /** Bump to re-arm the circuit's timer when the PAGE itself has not changed. */
  const [loopTick, setLoopTick] = useState(0);
  /** The last guest shown, so the next draw can avoid repeating it. */
  const lastGuest = useRef<PageId | null>(null);
  /** Set while an announcement owns the screen; the circuit does not run underneath it. */
  const [announcing, setAnnouncing] = useState(false);
  /** Set while a person is driving; cleared once they have been idle. */
  const [manual, setManual] = useState(false);
  // [PM-SG CANVAS 2026-08-13] How much of the canvas fits this screen, and the `d` diagnostic.
  const canvasFit = useCanvasFit();
  const [diag, setDiag] = useState(false);
  /* [PM-SG TEXT 2026-08-13] Read back from storage so the mode picked in front of the television
     survives the board's unattended deploy-reload. Anything unrecognised falls back to the
     original default rather than to a blank value. */
  const [smoothing, setSmoothing] = useState<Smoothing>(() => {
    try {
      return localStorage.getItem(SMOOTHING_KEY) === 'auto' ? 'auto' : 'antialiased';
    } catch { return 'antialiased'; }
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'd' || e.key === 'D') setDiag((v) => !v);
      if (e.key === 's' || e.key === 'S') {
        setSmoothing((v) => {
          const next: Smoothing = v === 'antialiased' ? 'auto' : 'antialiased';
          try { localStorage.setItem(SMOOTHING_KEY, next); } catch { /* private mode: session only */ }
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const manualTimer = useRef(0);
  /** [PM-SG 2026-08-13] The page that is live RIGHT NOW, readable from a callback that was built
   *  in some earlier render. `go` guards on this rather than on the `page` state — see the note
   *  on `go` for the freeze that cost. `go` is the only thing that calls setPage, so this ref and
   *  the state cannot drift. */
  const pageRef = useRef<PageId>('race');
  /** The page the circuit was on when an announcement interrupted it. */
  const resumePage = useRef<PageId>('race');
  /** The newest announcement the board has already shown, and the hold it is showing it for. */
  const lastAnn = useRef<number | null>(null);
  const annHold = useRef(0);
  /** [PM-SG SETTLE 2026-08-13] The new build id the board is currently sitting out, and when it
   *  was first seen. A ref, not state: nothing renders from it and the board must not re-render
   *  twice a minute for a version check. See SETTLE_MS. */
  const buildSeen = useRef<{ build: string; at: number } | null>(null);
  /** Serialised last-rendered board data — the guard that keeps an unchanged poll from
   *  re-rendering the view. See the note in `load`. */
  const raceSig = useRef('');

  /* [PM-SG DEBRIEF] The coaching cards, reachable from an effect that must NOT re-run when the
     board polls. Assigned on every render, read only on arrival at the debrief. */
  const coachingRef = useRef(race?.coaching ?? []);
  coachingRef.current = race?.coaching ?? [];

  const load = useCallback(async (): Promise<boolean> => {
    try {
      const board = await getJson<LeaderPayload>('/api/pm-singapore/leaderboard');
      /**
       * [PM-SG SMOOTH 2026-08-13] ONLY RE-RENDER WHEN SOMETHING ACTUALLY CHANGED.
       *
       * This polls every 30 seconds and used to call setRace() unconditionally. buildLeaderData
       * returns a fresh object every time, so React re-rendered the whole active view — a
       * thousand-line tree of styled-components — twice a minute whether a single figure had
       * moved or not. On a wall board that lands as a hitch in whatever animation is mid-flight,
       * every thirty seconds, forever. It is the most frequent avoidable main-thread work here.
       *
       * `updatedAt` is excluded from the comparison deliberately: it is the sync job's own
       * timestamp, it changes on every cycle, and — checked — it is not rendered anywhere on the
       * board. Leaving it in would make every payload look different and the skip would never
       * fire, which is exactly the trap this comment exists to stop someone walking back into.
       */
      const next = buildLeaderData(board);
      const { updatedAt: _ignored, ...rendered } = next;
      const sig = JSON.stringify(rendered);
      if (sig !== raceSig.current) {
        raceSig.current = sig;
        setRace(next);
      }
      lastOk.current = Date.now();
      misses.current = 0;
      setError(null);
      return true;
    } catch (e: any) {
      /* A 401 is not a blip and waiting it out helps nobody — the session is gone and somebody
         has to sign in again, so it is said at once. */
      if (e?.message === 'HTTP 401') {
        setError('The session has expired — sign in again.');
        return false;
      }
      misses.current += 1;
      const stale = lastOk.current ? Date.now() - lastOk.current : 0;
      /* Two rules, because "nothing on screen yet" and "what is on screen is going stale" are
         different problems. With no data at all a couple of misses is already worth saying;
         with figures up, only real staleness is. */
      if (lastOk.current ? stale > STALE_MS : misses.current >= 2) {
        setError('The dashboard will reconnect on the next cycle.');
      }
      return false;
    }
  }, []);

  const loadFeed = useCallback(async () => {
    try { setFeed(await getJson<AnnouncementPayload>('/api/pm-singapore/announcements?limit=40')); }
    catch { /* the feed is decoration on a board whose figures are already up */ }
  }, []);

  useEffect(() => {
    /* A missed poll re-tries in seconds rather than waiting out the whole cycle. The usual
       cause is the API restarting under it, and by the time the staleness window is anywhere
       near expiring this has already succeeded — which is why the banner now stays off the
       wall for the events that used to trigger it. */
    const tick = async () => {
      const ok = await load();
      window.clearTimeout(retry.current);
      if (!ok && !document.hidden) {
        retry.current = window.setTimeout(() => { void tick(); }, RETRY_MS);
      }
    };

    void tick();
    void loadFeed();
    const t = window.setInterval(() => {
      if (document.hidden) return; // a backgrounded tab should not poll for hours
      void tick();
      void loadFeed();
    }, POLL_MS);
    return () => { window.clearInterval(t); window.clearTimeout(retry.current); };
  }, [load, loadFeed]);

  /* [PM-SG GIF 2026-10-07] Fetch the GIF the first announcement will get while the board is
     quiet, so that card does not open on a GIF still downloading (the biggest is 6.5 MB). After
     the boot, so it never competes with the figures; NotifierView warms each next one itself. */
  useEffect(() => {
    const t = window.setTimeout(warmNextGif, 10000);
    return () => window.clearTimeout(t);
  }, []);

  /**
   * [PM-SG DEBRIEF 2026-09-18, user] STRICT RANK ORDER, walked three at a time.
   *
   * P4-P5-P6, then P7-P8-P9, then P10-P11-P12, and when the last name has been covered it starts
   * again from rank 4. The user's words: *"it will show data line wise only, from rank 4 to the
   * last, and after all the names get covered it will again start from the rank 4"*.
   *
   * ⚠ A SHUFFLED ORDER WAS BUILT HERE AND REMOVED THE SAME DAY. Randomising put P7, P11 and P5
   * on one page, and a page whose positions jump around reads as broken data rather than as
   * variety — which is exactly how it was reported. Rank order is the whole point of the page.
   *
   * [PM-SG DEBRIEF ONE-CARD 2026-09-21, user] The three are now shown ONE AT A TIME, each for
   * its share of the dwell (30s at 90s; a short last group of two gets 45s each rather than a
   * dead slot). The group is still chosen here, once per arrival.
   *
   * ⚠ CALLED FROM `go`, AT THE SWAP, NOT FROM AN EFFECT. It used to run in an effect keyed on
   * `page`, which meant the view's first render showed the PREVIOUS group for a frame. That was
   * hidden under the sweep — but the view now starts its card clock at mount, so the visit has
   * to be decided in the same batch as the page change or the clock starts on the wrong names.
   *
   * ⚠ THE GROUP (THREE RANKS) IS FIXED AT ARRIVAL. `race` is read through a ref so a poll never
   * re-picks it mid-visit. WHO fills each rank is the view's call, made live as each card comes
   * up (PitDebriefView holderOf) - the card on screen keeps its person until its share is up.
   *
   * ⚠ BACK FROM AN ANNOUNCEMENT = THE SAME VISIT, THE SAME CARD. Before, the return counted as
   * a fresh arrival and skipped the rest of the interrupted group. The hand-back sets
   * `debriefResume` and this reopens the frozen visit on the card that was up.
   *
   * Reads refs only and sets state, so it is stable — `go` depends on it and must stay stable.
   */
  const enterDebrief = useCallback(() => {
    const prev = debriefVisit.current;
    let v: DebriefVisit;
    if (debriefResume.current && prev && prev.cards.length) {
      v = { ...prev, visit: prev.visit + 1, start: Math.min(debriefCard.current, prev.cards.length - 1) };
    } else {
      const cards = coachingRef.current || [];
      const groups = Math.max(1, Math.ceil(cards.length / DEBRIEF_GROUP));
      /* Past the last group, round to rank 4 again. ⚠ Not a modulo: if the field shrank since the
         last visit (12 chasers down to 9), cursor 4 % 3 would land on P7-9 and skip P4-6. */
      const group = debriefPage.current < groups ? debriefPage.current : 0;
      debriefPage.current = group + 1;
      const picked = cards.slice(group * DEBRIEF_GROUP, (group + 1) * DEBRIEF_GROUP);
      v = {
        visit: (prev?.visit ?? 0) + 1,
        group,
        groups,
        cards: picked,
        start: 0,
        perCardMs: Math.round(DWELL.debrief / Math.max(1, picked.length)),
      };
    }
    debriefResume.current = false;
    debriefCard.current = v.start;
    debriefCardAt.current = Date.now();
    debriefVisit.current = v;
    setDebrief(v);
  }, []);

  /* Lift the boot screen once there is something real behind it — OR once a request has
     failed. Waiting only on `race` would leave a board that cannot reach its API showing
     "CONNECTING TO RACE CONTROL" for ever, with the error banner sealed underneath the very
     overlay that is hiding the problem. */
  useEffect(() => {
    /* Once only. Without the latch this re-arms on every poll — `race` is a new object each
       cycle — and the entry below would re-fire the arrival every thirty seconds. */
    if (booted.current) return;
    if (!race && !error) return;
    const t = window.setTimeout(() => {
      booted.current = true;
      setBooting(false);
      /**
       * [2026-08-12] AND THE ARRIVAL BELONGS HERE, NOT AT MOUNT.
       *
       * On a cold refresh the board mounts the moment the first payload lands, but the start-
       * lights overlay is still covering it for a second and a half. The cars therefore drove
       * in from the left UNDERNEATH the boot screen, and what the viewer actually saw was the
       * lights lift to reveal a field already parked — the one animation on the page that says
       * "this is live" spent its whole run hidden.
       *
       * Bumping the entry token at the moment the overlay lifts hands the arrival to the frame
       * the board becomes VISIBLE on. Every view keys its entrance off this same token, so the
       * pit wall's delivery run was losing itself behind the boot screen in exactly the same
       * way and is fixed by the same line.
       */
      setEntryToken((n) => n + 1);
    }, prefersReducedMotion() || error ? 0 : 1500);
    return () => window.clearTimeout(t);
  }, [race, error]);

  /* ── the unattended reload ────────────────────────────────────────────
     See the note at the top of this file for why this board is allowed to do what the rest of
     the app deliberately will not. */
  useEffect(() => {
    const mine = process.env.REACT_APP_BUILD;
    if (!mine) return; // dev build — no version stamp, nothing to compare against
    let cancelled = false;

    const check = async () => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok || cancelled) return;
        const { build } = await r.json();
        if (!build || build === mine) return;

        /**
         * [PM-SG SETTLE 2026-08-13] SIT THE NEW BUILD OUT BEFORE ACTING ON IT. See SETTLE_MS.
         *
         * First sighting only STARTS the clock — it never reloads. A build id that changes again
         * mid-hold (a second deploy on top of the first, or a rollback) restarts the clock rather
         * than inheriting the old one's elapsed time, so the board always gets a full quiet window
         * against whatever is actually being served now.
         *
         * ⚠ Deliberately ahead of the loop guard and the probe. Both are about "is this build
         * servable"; asking that question while the deploy is still writing is how you get a pass
         * that means nothing. Settle first, then check.
         */
        const seen = buildSeen.current;
        if (!seen || seen.build !== build) { buildSeen.current = { build, at: Date.now() }; return; }
        if (Date.now() - seen.at < SETTLE_MS) return;

        /**
         * LOOP GUARD — if we already reloaded towards this build and are still not running it,
         * the deploy is half-served, so back off rather than reloading for ever unwatched.
         *
         * ⚠ [2026-08-12] IT USED TO BACK OFF FOR EVER. A single half-served deploy — a cached
         * index.html, a chunk still being written — permanently blacklisted that build id, and
         * the only cure was a human refreshing a television. The guard now expires: it still
         * cannot loop (one attempt per five minutes at worst), but the board always heals
         * itself in the end instead of waiting for somebody to walk over to it.
         */
        const guard = (sessionStorage.getItem(RELOAD_KEY) || '').split('|');
        if (guard[0] === build && Date.now() - Number(guard[1] || 0) < RELOAD_RETRY_MS) return;

        // Confirm the new build actually serves before reloading into it: the deploy rewrites
        // build/ in place, so version.json can advertise a version whose chunks are still
        // being written.
        const probe = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
        if (!probe.ok || cancelled) return;

        sessionStorage.setItem(RELOAD_KEY, `${build}|${Date.now()}`);

        /* Flag first, reload underneath it. The overlay is deliberately NOT torn down before the
           reload — it stays painted until the new document replaces the whole page, which is the
           only way to cover the white gap while the new bundle loads. */
        if (prefersReducedMotion()) { window.location.reload(); return; }
        setFlagging(true);
        window.setTimeout(() => window.location.reload(), FLAG_MS);
      } catch { /* offline or mid-deploy — try again on the next cycle */ }
    };

    const t = window.setInterval(check, VERSION_POLL_MS);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);

  /* ── view switching ──────────────────────────────────────────────── */
  const go = useCallback((id: PageId) => {
    /**
     * ⚠ [PM-SG 2026-08-13] THE GUARD READS A REF, AND `go` MUST STAY STABLE. DO NOT PUT `page`
     * BACK IN THE DEPS.
     *
     * This used to close over the `page` state with `[page]` in the deps, and that froze the
     * board on NOTIFIER after every single announcement. The announcement arms its ANNOUNCE_MS
     * hold and then calls `go(resumePage.current)` when it fires — but the timeout keeps the
     * `go` from the render that armed it, whose captured `page` is EXACTLY the page it is trying
     * to return to. `id === page` was therefore always true, the resume was always a silent
     * no-op, and the board sat on the announcement until the next one arrived or somebody
     * touched the dock. Every callback here that outlives the render that made it — the hold,
     * the manual idle timer, the dwell timer — walks into the same trap, so the guard reads live
     * state and the deps hold nothing but the stable `enterDebrief`.
     *
     * The ref is written here, at the moment the change is accepted, rather than from an effect:
     * the swap itself is 900ms behind the transition, and a ref that only caught up on commit
     * would let two calls in the same tick both pass the guard.
     */
    if (id === pageRef.current) return;
    pageRef.current = id;
    /* [PM-SG DEBRIEF ONE-CARD] The debrief's visit is decided in the same batch as the page
       change - see enterDebrief. Any other arrival spends a pending resume, so a stale one can
       never reopen an old visit later. */
    const arrive = () => {
      if (id === 'debrief') enterDebrief();
      else debriefResume.current = false;
      /* The page the board actually LANDED on is what an announcement hands back to. The
         announcement reads the `page` state, which during a 900ms sweep is still the page being
         left - an announcement landing then would reopen a debrief that had just finished, or
         send the board back to a page it had already left. This runs after the announcement's
         own write, so it corrects it. NOTIFIER is never a page to hand back to. */
      if (id !== 'notifier') resumePage.current = id;
      setPage(id);
      setEntryToken((n) => n + 1);
    };
    if (prefersReducedMotion()) {
      arrive();
      return;
    }
    /* Random of the five, but never the same one twice running — a repeat reads as the board
       stuttering rather than as variety. */
    let pick = Math.floor(Math.random() * SWEEP_KINDS);
    if (pick === sweepKind.current) pick = (pick + 1) % SWEEP_KINDS;
    sweepKind.current = pick;
    setSweep({ page: id, kind: pick });
    /* The swap happens while the transition is fully covering the screen: early enough that the
       outgoing page is already hidden, late enough that the incoming one gets a frame to lay
       itself out before it is uncovered. */
    window.setTimeout(arrive, SWEEP_COVER_MS);
    window.setTimeout(() => setSweep(null), SWEEP_MS);
  }, [enterDebrief]);

  /** A person touched the dock: they own the board until they have been idle. */
  const goManual = useCallback((id: PageId) => {
    setManual(true);
    window.clearTimeout(manualTimer.current);
    manualTimer.current = window.setTimeout(() => {
      setManual(false);
      setLoopTick((n) => n + 1); // re-arm from wherever they left the board
    }, MANUAL_IDLE_MS);
    go(id);
  }, [go]);

  useEffect(() => () => window.clearTimeout(manualTimer.current), []);

  /**
   * [PM-SG 2026-08-12] THE CIRCUIT. The board drives itself.
   *
   *   RACE → a guest → RACE → a DIFFERENT guest → RACE → …
   *
   * RACE is the hub rather than one stop in a ring: it is the only page that answers "where is
   * everyone", so the board always comes back to it instead of leaving the room on a table of
   * figures. The guest is drawn at random from PIT WALL / TIMING / PARC FERMÉ, never the same one
   * twice running, so a passer-by cannot predict what is coming — a board whose next screen you
   * can guess is a board you stop looking at.
   *
   * Each switch bumps the entry token, so every page replays its entrance: the cars drive in from
   * the left on RACE, the lamps light and the delivery runs again on PIT WALL, the top three park
   * again on PARC FERMÉ. A page that arrived silently would look like one that had been sitting
   * there all along.
   *
   * THREE THINGS STOP THE CIRCUIT, and each hands it back:
   *   BOOTING     nothing runs until the board is actually on screen
   *   ANNOUNCING  a new gain owns the screen for a minute; the circuit resumes on the page it
   *               was interrupted on, not at some later stop
   *   MANUAL      a person at the dock owns it until they have been idle for a minute
   *
   * ⚠ `loopTick` is in the deps for a specific reason: whenever the circuit needs re-arming
   * WITHOUT the page changing — resuming after an announcement that returned to the same page,
   * or after a manual session ends — `go` returns early, `page` never changes, and without a
   * second dependency the effect would not re-run. The circuit would stall for ever.
   */
  useEffect(() => {
    if (booting || announcing || manual) return;
    /* ⚠ NOTIFIER used to bail out here, on the grounds that the announcement hold owned its own
       clock. It does — but only while `announcing` is true, which the line above already covers.
       Outside that, bailing made NOTIFIER the one page with nothing holding a clock: if the hold
       ever handed back without the page actually changing, the board stopped for good. That is
       precisely what the stale-closure bug in `go` did, every time, from the day the announcement
       interrupt was added. The dwell now applies to NOTIFIER like any other page, so even if the
       hand-back fails the circuit walks the board off it a minute later instead of freezing.
       Belt and braces — keep both. */

    /* [PM-SG DEBRIEF ONE-CARD] The debrief holds for the cards it has LEFT, not a flat dwell: a
       fresh visit is all three (90s), but one resumed after an announcement on its third card
       needs only that card's 30s. Anything longer would sit on the last card. The card on screen
       counts only what is left of its share - when the circuit re-arms mid-card (a person at the
       dock going idle), a full share on top would hold the last card for up to a minute. */
    const v = debriefVisit.current;
    const dwell = page === 'debrief' && v && v.cards.length
      ? Math.max(0, v.cards.length - 1 - debriefCard.current) * v.perCardMs
        + Math.max(0, v.perCardMs - (Date.now() - debriefCardAt.current))
      : DWELL[page] ?? 90000;

    const t = window.setTimeout(() => {
      if (page !== 'race') { go('race'); return; }
      const pool = GUESTS.filter((g) => g !== lastGuest.current);
      const next = pool[Math.floor(Math.random() * pool.length)] || GUESTS[0];
      lastGuest.current = next;
      go(next);
    }, dwell);

    return () => window.clearTimeout(t);
  }, [page, loopTick, booting, announcing, manual, go]);

  /**
   * [PM-SG 2026-08-12] A NEW ANNOUNCEMENT INTERRUPTS THE ROTATION.
   *
   * This board's standing rule has been that nothing interrupts a wall display on its own, and
   * that rule is right for anything recurring. An announcement is the exception the user asked
   * for and it earns it: retention or an upsell landing is the single most newsworthy thing that
   * happens here, it happens a handful of times a day, and a board that waits four minutes to
   * mention it is not announcing anything.
   *
   * The head of the feed is the trigger. The first payload only PRIMES the reference — every
   * event is "new" on the first render, and cutting to the announcement the moment the board
   * boots would be a lie about when it happened.
   *
   * It holds for the announcement's own dwell and then rejoins the circuit at the next stop
   * rather than dropping back where it was, so the interruption costs the rotation one page
   * instead of restarting it.
   */
  useEffect(() => {
    const head = feed?.events?.[0]?.id ?? null;
    if (head === null) return;
    if (lastAnn.current === null) { lastAnn.current = head; return; }
    if (head === lastAnn.current) return;
    lastAnn.current = head;

    /* Remember what was on screen so the circuit can be handed back exactly where it was, and
       hold the circuit still while the announcement is up. */
    if (page !== 'notifier') resumePage.current = page;
    setAnnouncing(true);
    go('notifier');

    window.clearTimeout(annHold.current);
    annHold.current = window.setTimeout(() => {
      setAnnouncing(false);
      /* [PM-SG DEBRIEF ONE-CARD] Handing back to the debrief reopens the SAME visit on the SAME
         card. Only when the board is really on NOTIFIER - if a person moved it meanwhile, `go`
         returns early and the flag would outlive the hand-back it was meant for. */
      if (resumePage.current === 'debrief' && pageRef.current === 'notifier') {
        debriefResume.current = true;
      }
      go(resumePage.current);
      setLoopTick((n) => n + 1); // re-arm even if the page it returns to is the one showing
    }, ANNOUNCE_MS);
  }, [feed, go, page]);

  useEffect(() => () => window.clearTimeout(annHold.current), []);

  const month = race?.month || monthLabel(null);

  return createPortal(
    <>
      <LockScroll />
      {/* [PM-SG CANVAS] Frame = the screen and its letterbox bars. Stage = the 1920x1080 canvas
          everything is composed on, scaled to fit. Everything that is BOARD content lives inside
          the Stage so it scales as one piece; only the build flag stays outside (see below). */}
      <Frame>
      <Stage $scale={canvasFit.scale} $zoom={CAN_ZOOM} $width={canvasFit.width}>
      <Board $smoothing={smoothing}>
        {booting && (
          <Boot>
            {!prefersReducedMotion() && (
              <div className="lights" aria-hidden="true"><i /><i /><i /><i /><i /></div>
            )}
            <span>{race ? 'LIGHTS OUT' : 'CONNECTING TO RACE CONTROL'}</span>
          </Boot>
        )}

        {error && (
          <Banner $kind="err">
            <span>CONNECTION LOST — RETRYING</span>
            <span className="note">{error}</span>
          </Banner>
        )}

        {race && page === 'race' && <RaceView data={race} entryToken={entryToken} />}
        {race && page === 'pitwall' && <PitWallView kpis={race.kpis} entryToken={entryToken} />}
        {race && page === 'timing' && <TimingView data={race} />}
        {race && page === 'podium' && <PodiumView data={race} active={page === 'podium'} />}
        {/* ⚠ Gated on `race` like every other view. The hall was the one page that was not,
                  because it carried its own payload; the debrief reads data.coaching and would
                  render against null on the first frame after boot. */}
              {race && page === 'debrief' && debrief && (
                <PitDebriefView
                  key={debrief.visit}
                  data={race}
                  entryToken={entryToken}
                  visit={debrief}
                  onCard={onDebriefCard}
                />
              )}
        {race && page === 'notifier' && (
          <NotifierView feed={feed} month={month} active={page === 'notifier'} />
        )}
      </Board>

      {sweep && (
        <Sweep aria-hidden="true" $kind={sweep.kind}>
          <div className="band">
            {sweep.kind === 3 && (<><i className="half t" /><i className="half b" /></>)}
            {sweep.kind === 4 && <i className="puff" />}
            {sweep.kind === 2 && (
              <div className="lamps"><i /><i /><i /><i /><i /></div>
            )}
            <span className="t">{PAGES.find((p) => p.id === sweep.page)?.label || ''}</span>
            <span className="prog"><i /></span>
          </div>
        </Sweep>
      )}
      {/* goManual, not go: a dock press pauses the circuit until the person has been idle. */}
      <NavDock current={page} onSelect={goManual} />
      </Stage>
      </Frame>

      {/* ⚠ The flag stays OUTSIDE the Stage on purpose. Its whole job is that nothing shows
          through it — including the letterbox bars and the floating profile circle at z-1200,
          both of which live outside the canvas. Being outside, it gets the stage scale passed in
          by hand so its squares and its caption still grow with the screen. */}
      {flagging && (
        <Chequer aria-hidden="true" $scale={canvasFit.scale}>
          {/* The cloth. Each panel is offset horizontally so the chequer stays continuous across
              the seams, and started slightly later than the one before it so the ripple travels
              left to right instead of the whole screen pulsing at once. */}
          <div className="cloth">
            {Array.from({ length: FLAG_STRIPS }, (_, i) => (
              <i
                key={i}
                style={{
                  left: `calc(${i} * 100% / ${FLAG_STRIPS})`,
                  backgroundPositionX: `${-(i * window.innerWidth) / FLAG_STRIPS}px`,
                  animationDelay: `${-i * FLAG_WAVE_STEP_MS}ms`,
                }}
              />
            ))}
          </div>
          <span>NEW BUILD — RACE CONTROL UPDATING</span>
        </Chequer>
      )}

      {diag && <Diagnostics fit={canvasFit} smoothing={smoothing} />}
    </>,
    document.body,
  );
};

export default PMSingaporePage;
